import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  GlobalStyles,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";
import { API_BASE_URL, SUBSCRIPTION_CONFIG, AUTH_CONFIG } from "@/config";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

const stripePromise = SUBSCRIPTION_CONFIG.publishableKey
  ? loadStripe(SUBSCRIPTION_CONFIG.publishableKey, {
      developerTools: {
        assistant: {
          enabled: false, // Disable the floating Stripe developer tools widget
        },
      },
    })
  : null;

const featureList = [
  "Real music by real artists",
  "No algorithmic interruptions",
  "Early access for real fans",
  "Lossless audio by default",
  "Exclusive content drops",
  "Pay the artists you love directly",
  "Create and share your Mixtapes",
];

const SubscribePage: React.FC = () => {
  const { user, token, loading, hasActiveSubscription, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "true";

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [sessionPriceId, setSessionPriceId] = useState<string | null>(null);
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<"success" | "error" | null>(null);
  const [subscriptionMessage, setSubscriptionMessage] = useState<string | null>(null);

  const successUrl = useMemo(
    () =>
      `${window.location.origin}/payment-success?flow=${SUBSCRIPTION_CONFIG.successFlowParam}`,
    []
  );
  const elementOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    return {
      clientSecret,
      appearance: {
        theme: "night" as const,
        labels: "floating" as const,
        variables: {
          colorPrimary: "#FFFFFF",
          colorPrimaryText: "#180021",
          colorText: "#FFFFFF",
          colorTextSecondary: "#FFFFFF",
          colorTextPlaceholder: "#B7B2D3",
          colorBackground: "transparent",
          colorDanger: "#ff4d4f",
          colorIcon: "#FFFFFF",
          borderRadius: "12px",
          spacingUnit: "6px",
          fontFamily: '"Poppins", sans-serif',
        },
        rules: {
          ".Block": {
            backgroundColor: "transparent",
            border: "1px solid rgba(255,255,255,0.25)",
            boxShadow: "none",
          },
          ".Input": {
            backgroundColor: "rgba(8,0,15,0.35)",
            border: "1px solid rgba(255,255,255,0.5)",
            color: "#FFFFFF",
          },
          ".Input--invalid": {
            borderColor: "#ff4d4f",
          },
          ".Label": {
            color: "#FFFFFF",
            fontWeight: "500",
            letterSpacing: "0.5px",
          },
          ".Tab": {
            color: "#FFFFFF",
            border: "1px solid rgba(255,255,255,0.25)",
            backgroundColor: "rgba(0,0,0,0.25)",
          },
          ".Tab--selected": {
            borderColor: "#E4FF1A",
            backgroundColor: "rgba(0,0,0,0.45)",
          },
          ".TabLabel": {
            color: "#FFFFFF",
            fontWeight: "600",
          },
          ".TabLabel--selected": {
            color: "#FFFFFF",
          },
          ".TabIcon": {
            color: "#FFFFFF",
          },
          ".TabIcon--selected": {
            color: "#FFFFFF",
          },
          ".Link": {
            color: "#E4FF1A",
          },
          ".Error": {
            color: "#ff4d4f",
          },
        },
      },
    };
  }, [clientSecret]);

  const handleCheckoutCancel = () => {
    setClientSecret(null);
    setCheckoutError(null);
    setCustomerId(null);
    setSessionPriceId(null);
  };

  const handleCheckoutSuccess = async () => {
    console.log("handleCheckoutSuccess called");
    setProcessingSuccess(true);
    setCheckoutError(null);
    
    try {
      // Refresh profile to get updated subscription status
      console.log("Refreshing profile...");
      await refreshProfile();
      console.log("Profile refreshed, waiting before second refresh...");
      
      // Give React time to update state before checking
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Force one more refresh to be absolutely sure
      console.log("Second profile refresh...");
      await refreshProfile();
      console.log("Profile refreshed again");
      
      // Check if subscription is now active
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Show success message
      setSubscriptionStatus("success");
      setSubscriptionMessage("Welcome to Lsnto! Your subscription has been successfully activated and we are excited to have you on board.");
      setProcessingSuccess(false);
    } catch (err) {
      console.error("Error in handleCheckoutSuccess:", err);
      setSubscriptionStatus("error");
      setSubscriptionMessage("Your payment was processed, but there was an issue updating your account. Please refresh the page or contact support if the issue persists.");
      setProcessingSuccess(false);
    }
  };

  const handleContinueAfterSuccess = () => {
    navigate(AUTH_CONFIG.loginRedirectPath, { replace: true });
  };

  const handleRetryAfterError = () => {
    setSubscriptionStatus(null);
    setSubscriptionMessage(null);
    setError(null);
    setCheckoutError(null);
    setClientSecret(null);
    setCustomerId(null);
    setSessionPriceId(null);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    // Don't auto-navigate if we're showing success/error screen or processing
    if (hasActiveSubscription && !subscriptionStatus && !processingSuccess) {
      navigate(AUTH_CONFIG.loginRedirectPath, { replace: true });
    }
  }, [loading, user, hasActiveSubscription, navigate, subscriptionStatus, processingSuccess]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Prevent duplicate subscriptions
    if (hasActiveSubscription) {
      setError("You already have an active subscription.");
      return;
    }

    if (!stripePromise) {
      setError("Stripe is not configured. Please try again later.");
      return;
    }

    setSubmitting(true);
    setCheckoutError(null);
    setError(null);

    try {
      const requestBody = {
        user_id: user.id,
        price_id: SUBSCRIPTION_CONFIG.standardPriceId,
        metadata: {
          user_id: user.id,
          type: "Standard",
        },
      };
      
      console.log("Creating subscription session with:", requestBody);
      console.log("User object:", user);
      console.log("Token present:", !!token);
      
      const response = await fetch(`${API_BASE_URL}/stripe/create-inline-subscription-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Unable to start checkout. Please try again.");
      }

      const data = await response.json();
      if (!data.clientSecret || !data.customerId) {
        throw new Error("Incomplete checkout session received from server.");
      }

      setClientSecret(data.clientSecret);
      setCustomerId(data.customerId);
      setSessionPriceId(data.priceId || SUBSCRIPTION_CONFIG.standardPriceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start checkout.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || (!user && !loading)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show processing state
  if (processingSuccess) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 2 }}>
        <CircularProgress sx={{ color: "#E4FF1A" }} />
        <Typography sx={{ color: "#E4FF1A", fontSize: "1.2rem" }}>
          Processing your subscription... Please wait.
        </Typography>
      </Box>
    );
  }

  // Show success/error message with manual continue button
  if (subscriptionStatus) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,rgb(47, 0, 148) 0%,rgb(182, 9, 64) 70%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              background: "rgba(0, 0, 0, 0.7)",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              color: "white",
            }}
          >
            {subscriptionStatus === "success" ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#E4FF1A",
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    ✓ Subscription Successful!
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem", opacity: 0.9 }}>
                    {subscriptionMessage}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleContinueAfterSuccess}
                  sx={{
                    backgroundColor: "#E4FF1A",
                    color: "#000",
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "#d4ef0a",
                    },
                  }}
                >
                  Continue to Lsnto to discover new music
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#ff4d4f",
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    ⚠ Subscription Issue
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem", opacity: 0.9 }}>
                    {subscriptionMessage}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={handleRetryAfterError}
                    sx={{
                      borderColor: "#E4FF1A",
                      color: "#E4FF1A",
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      "&:hover": {
                        borderColor: "#d4ef0a",
                        backgroundColor: "rgba(228, 255, 26, 0.1)",
                      },
                    }}
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleContinueAfterSuccess}
                    sx={{
                      backgroundColor: "#E4FF1A",
                      color: "#000",
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#d4ef0a",
                      },
                    }}
                  >
                    Continue Anyway
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Container>
      </Box>
    );
  }

const showCheckout = Boolean(
  clientSecret &&
  stripePromise &&
  elementOptions &&
  customerId &&
  (sessionPriceId || SUBSCRIPTION_CONFIG.standardPriceId)
);

  return (
    <>
      <GlobalStyles
        styles={{
          "@import": 'url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap")',
          body: { fontFamily: '"Poppins", sans-serif' },
        }}
      />
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,rgb(47, 0, 148) 0%,rgb(182, 9, 64) 70%)",
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          py: { xs: 2, md: 0 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            transform: { xs: "none", md: "scale(0.75)" },
            transformOrigin: { xs: "top center", md: "top center" },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 3, md: 2 }}
            alignItems="center"
            sx={{
              display: {
                xs: showCheckout ? "flex" : "none",
                md: "flex",
              },
            }}
          >
            <Box
              sx={{
                flex: 1.4,
                width: "100%",
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "center",
                order: { xs: 2, md: 1 },
                mt: { xs: 0, md: -4 },
              }}
            >
              <Box
                component="img"
                src="/LsntoSubHeadphones.png"
                alt="Listener enjoying music"
                sx={{
                  width: { xs: "70%", sm: "80%", md: "130%", lg: "150%" },
                  maxWidth: { xs: 260, sm: 320, md: 900 },
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                color: "white",
                textAlign: { xs: "center", md: "left" },
                order: { xs: 1, md: 2 },
                px: { xs: 1.5, md: 0 },
              }}
            >
              {showCheckout ? (
                <>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      color: "#E4FF1A",
                    }}
                  >
                    Secure Checkout
                  </Typography>
                  <Typography sx={{ mb: 3, opacity: 0.85 }}>
                    Enter your payment details to complete your subscription. All payments are processed securely by Stripe.
                  </Typography>
                  {checkoutError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {checkoutError}
                    </Alert>
                  )}
                  {stripePromise && elementOptions && customerId ? (
                    <Elements
                      stripe={stripePromise}
                      options={elementOptions}
                      key={clientSecret ?? "stripe-elements"}
                    >
                      <InlineCheckoutForm
                        customerId={customerId}
                        priceId={sessionPriceId || SUBSCRIPTION_CONFIG.standardPriceId}
                        token={token || undefined}
                        metadata={{
                          user_id: user?.id,
                          type: "Standard",
                        }}
                        onSuccess={handleCheckoutSuccess}
                        onCancel={handleCheckoutCancel}
                        onError={(message) => {
                          setCheckoutError(message);
                          setSubscriptionStatus("error");
                          setSubscriptionMessage(message || "There was an error processing your payment. Please try again.");
                        }}
                        returnUrl={successUrl}
                      />
                    </Elements>
                  ) : (
                    <Alert severity="error">
                      Unable to initialize Stripe. Please refresh the page or contact support.
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      mb: 2,
                      color: "#E4FF1A",
                      textShadow: "0 6px 30px rgba(0,0,0,0.45)",
                      fontSize: { xs: "2.8rem", md: "3.8rem" },
                      lineHeight: 1.05,
                    }}
                  >
                    Join the music revolution today!
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: "white",
                      fontWeight: 700,
                    }}
                  >
                    Standard Subscription ·{" "}
                    <Box component="span" sx={{ fontSize: "2.2rem", fontWeight: 800 }}>
                      £5.99
                    </Box>
                    /mo
                  </Typography>

                  <Typography variant="subtitle1" sx={{ mb: 2, opacity: 0.85 }}>
                    Why Subscribe to Lsnto?
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      pl: { xs: 0, md: 3 },
                      mb: 3,
                      listStyle: { xs: "none", md: "disc" },
                      "& li": { mb: 0.75, opacity: 0.9 },
                    }}
                  >
                    {featureList.map((feature) => (
                      <li key={feature}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: "0.9rem", md: "1rem" },
                            textAlign: { xs: "left", md: "left" },
                          }}
                        >
                          {feature}
                        </Typography>
                      </li>
                    ))}
                  </Box>

                  {cancelled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Checkout was cancelled. You can try again whenever you're ready.
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      mb: 3,
                      flexWrap: "wrap",
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleSubscribe}
                      disabled={submitting || hasActiveSubscription}
                      sx={{
                        py: { xs: 1.1, md: 1.5 },
                        px: { xs: 3, md: 4 },
                        fontWeight: 600,
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        textTransform: "none",
                        borderColor: "#E4FF1A",
                        color: "#E4FF1A",
                        borderWidth: 2,
                        borderRadius: "26px",
                        backgroundColor: "transparent",
                        transition: "all 0.3s ease",
                        mx: { xs: "auto", md: 0 },
                        "&:hover": {
                          backgroundColor: "#E4FF1A",
                          color: "#180021",
                          borderColor: "#E4FF1A",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {submitting ? <CircularProgress size={24} sx={{ color: "#180021" }} /> : "Subscribe Now"}
                    </Button>
                  </Stack>

                  <Typography variant="body2" sx={{ opacity: 0.5 }}>
                    One verified Lsnto standard subscription for £5.99 / month. Cancel anytime. This
                    price only valid via the Lsnto web or mobile app and prices elsewhere may differ.{" "}
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => window.open("/terms-of-service", "_blank")}
                    >
                      Terms apply
                    </Typography>
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
          {/* Mobile‑only layout matching Flutter subscription card */}
          <Box
            sx={{
              display: { xs: showCheckout ? "none" : "flex", md: "none" },
              minHeight: "100vh",
              alignItems: "flex-end",
              justifyContent: "center",
              width: "100%",
              pb: 3,
              backgroundImage: 'url("/LsntoSubHeadphones.png")',
              backgroundSize: "cover",
              backgroundPosition: "center 20%",
              backgroundRepeat: "no-repeat",
            }}
          >
            <Box sx={{ position: "relative", width: "100%", px: 2 }}>
              {/* Floating heading */}
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#E4FF1A",
                    fontWeight: 700,
                    fontSize: 32,
                    lineHeight: 0.9,
                  }}
                >
                  Join the music
                </Typography>
                <Typography
                  sx={{
                    color: "#E4FF1A",
                    fontWeight: 700,
                    fontSize: 28,
                    lineHeight: 0.9,
                  }}
                >
                  revolution today!
                </Typography>
              </Box>

              {/* Purple card container */}
              <Box
                sx={{
                  backgroundColor: "rgba(42, 8, 96, 0.70)",
                  borderRadius: 2,
                  px: 3,
                  pt: 6,
                  pb: 3,
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    textAlign: "center",
                    mb: 1,
                    fontSize: "1rem",
                  }}
                >
                  Standard Subscription ·{" "}
                  <Box component="span" sx={{ fontSize: "1.4rem", fontWeight: 800 }}>
                    £5.99
                  </Box>
                  /mo
                </Typography>
                <Typography
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    textAlign: "center",
                    mb: 2,
                    fontSize: "1.4rem",
                  }}
                >
                  Why Subscribe to Lsnto?
                </Typography>

                {/* Feature list */}
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 2 }}>
                  {featureList.map((feature) => (
                    <Box
                      key={feature}
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 0.75,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: "white",
                          fontSize: 18,
                          mr: 1,
                          lineHeight: 1.2,
                        }}
                      >
                        •
                      </Box>
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: "0.95rem",
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Subscribe button */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSubscribe}
                    disabled={submitting || hasActiveSubscription}
                    sx={{
                      py: 0.9,
                      px: 3,
                      fontWeight: 600,
                      fontSize: "1rem",
                      textTransform: "none",
                      borderColor: "#E4FF1A",
                      color: "#E4FF1A",
                      borderWidth: 2,
                      borderRadius: "30px",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#E4FF1A",
                        color: "#180021",
                        borderColor: "#E4FF1A",
                      },
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={24} sx={{ color: "#180021" }} />
                    ) : (
                      "Subscribe Now"
                    )}
                  </Button>
                </Box>

                {/* Terms text */}
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    textAlign: "center",
                    fontSize: "0.75rem",
                  }}
                >
                  One verified Lsnto standard subscription for £5.99 / month. Cancel anytime. Prices may differ elsewhere. Terms apply.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SubscribePage;

type InlineCheckoutFormProps = {
  onSuccess: () => Promise<void> | void;
  onCancel: () => void;
  onError: (message: string) => void;
  returnUrl: string;
  customerId: string;
  priceId: string;
  token?: string;
  metadata?: Record<string, unknown>;
};

const InlineCheckoutForm: React.FC<InlineCheckoutFormProps> = ({
  onSuccess,
  onCancel,
  onError,
  returnUrl,
  customerId,
  priceId,
  token,
  metadata,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setLoading(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment method confirmation failed. Please try again.");
        setLoading(false);
        return;
      }

      if (!setupIntent || setupIntent.status !== "succeeded") {
        onError("Unable to confirm your payment method. Please try again.");
        setLoading(false);
        return;
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent.payment_method?.id;

      if (!paymentMethodId) {
        onError("Unable to retrieve payment method. Please try again.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/stripe/confirm-inline-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          customer_id: customerId,
          payment_method_id: paymentMethodId,
          price_id: priceId,
          metadata,
        }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        onError(result.error || "Unable to confirm your subscription. Please try again.");
        setLoading(false);
        return;
      }

      const result = await response.json();
      
      console.log("Subscription confirmation result:", result);

      if (result.clientSecret) {
        // Payment intent needs to be confirmed
        const confirmation = await stripe.confirmCardPayment(result.clientSecret, {
          payment_method: paymentMethodId,
        });

        if (confirmation.error) {
          onError(confirmation.error.message || "Unable to complete payment confirmation.");
          setLoading(false);
          return;
        }

        if (confirmation.paymentIntent?.status !== "succeeded") {
          onError("Payment confirmation is still pending. Please check again shortly.");
          setLoading(false);
          return;
        }
        
        console.log("Payment confirmed successfully:", confirmation.paymentIntent);
        
        // After payment succeeds, fetch the updated subscription status
        // The subscription should now be active after payment confirmation
        let subscriptionActive = false;
        
        try {
          const statusResponse = await fetch(`${API_BASE_URL}/stripe/subscription/${result.subscriptionId}`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            console.log("Updated subscription status:", statusData.status);
            
            // If subscription is now active or trialing, proceed
            if (["active", "trialing"].includes(statusData.status)) {
              console.log("Subscription is active, proceeding...");
              subscriptionActive = true;
            } else if (statusData.status === "incomplete") {
              // Payment succeeded but subscription still incomplete - wait and retry
              console.warn("Subscription still incomplete after payment, waiting...");
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              // Check one more time
              const retryResponse = await fetch(`${API_BASE_URL}/stripe/subscription/${result.subscriptionId}`, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              });
              
              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                console.log("Retry subscription status:", retryData.status);
                if (["active", "trialing"].includes(retryData.status)) {
                  subscriptionActive = true;
                } else {
                  console.warn("Subscription still not active after retry:", retryData.status);
                  // Payment succeeded, so proceed anyway - Stripe webhooks will update it
                  subscriptionActive = true;
                }
              } else {
                console.warn("Could not fetch subscription status on retry, but payment succeeded - proceeding");
                subscriptionActive = true;
              }
            } else {
              console.warn(`Unexpected subscription status: ${statusData.status}, but payment succeeded - proceeding`);
              // Payment succeeded, so proceed anyway
              subscriptionActive = true;
            }
          } else {
            // If we can't fetch status, assume success since payment succeeded
            console.warn("Could not fetch subscription status, but payment succeeded - proceeding");
            subscriptionActive = true;
          }
        } catch (statusError) {
          console.error("Error fetching subscription status:", statusError);
          // Payment succeeded, so proceed optimistically
          subscriptionActive = true;
        }
        
        // If payment succeeded, always proceed to success (Stripe webhooks will update subscription)
        if (!subscriptionActive) {
          console.warn("Subscription status check inconclusive, but payment succeeded - proceeding anyway");
        }
      } else if (!["active", "trialing"].includes(result.status)) {
        // No payment was required, but subscription is not active
        console.warn("Subscription status:", result.status);
        if (result.status === "incomplete" || result.status === "incomplete_expired") {
          onError("Payment could not be processed. Please try again with a different payment method.");
          setLoading(false);
          return;
        }
        onError("Subscription is still pending. Please refresh once payment completes.");
        setLoading(false);
        return;
      }

      // Payment succeeded, always call onSuccess to refresh profile and navigate
      console.log("Payment successful, calling onSuccess to refresh profile and navigate");
      try {
        await onSuccess();
        console.log("onSuccess completed successfully");
      } catch (successError) {
        console.error("Error in onSuccess callback:", successError);
        // Even if onSuccess fails, show error but don't block - user can refresh
        onError("Payment succeeded but there was an issue updating your account. Please refresh the page.");
      }
    } catch (err) {
      console.error("Inline checkout error:", err);
      onError("Something went wrong while completing your subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        backgroundColor: "transparent",
        border: "none",
        borderRadius: 2,
        p: 3,
      }}
    >
      <PaymentElement />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          sx={{
            flex: 2,
            bgcolor: "#E4FF1A",
            color: "#180021",
            fontWeight: "bold",
            py: 1.2,
            borderRadius: "10px",
            "&:hover": { bgcolor: "#d4f117" },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#180021" }} /> : "Start Membership"}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{
            flex: 1,
            maxWidth: "180px",
            borderColor: "#E4FF1A",
            borderWidth: 2,
            borderRadius: "10px",
            color: "#E4FF1A",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "#E4FF1A",
              color: "#180021",
              borderColor: "#E4FF1A",
            },
          }}
        >
          Back
        </Button>
      </Stack>
    </Box>
  );
};
